import { useState, useEffect } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Loader2, RefreshCw, ArrowRight, AlertCircle, Copy } from 'lucide-react'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import { formatFileSize } from '@/lib/schemas'
import { toast } from 'sonner'

/** 'DD Month YYYY' for the existing document's upload date; empty when unknown. */
function formatUploadDate(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * F4 — a same-name upload into a folder that already holds a live document of
 * that name is a decision, not a failure. Two live copies both feed AI
 * retrieval, so a superseded policy can be cited alongside its replacement;
 * this dialog makes the user pick before either outcome exists.
 *
 * Resolves one collision at a time (multi-file uploads queue behind it).
 */
export function DuplicateUploadDialog() {
  const duplicateUploads = useLibraryStore((s) => s.duplicateUploads)
  const resolveDuplicateAsNewVersion = useLibraryStore((s) => s.resolveDuplicateAsNewVersion)
  const resolveDuplicateKeepBoth = useLibraryStore((s) => s.resolveDuplicateKeepBoth)
  const dismissDuplicateUpload = useLibraryStore((s) => s.dismissDuplicateUpload)

  const pending = duplicateUploads[0]
  const [isWorking, setIsWorking] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Reset transient state whenever a different collision reaches the head of
  // the queue, so file 2 never inherits file 1's error.
  useEffect(() => {
    setIsWorking(false)
    setActionError(null)
  }, [pending?.id])

  if (!pending) return null

  const { existing, file } = pending
  const uploadedOn = formatUploadDate(existing.createdAt)

  const handleNewVersion = async () => {
    setIsWorking(true)
    setActionError(null)
    try {
      await resolveDuplicateAsNewVersion(pending.id)
      toast.success('New version uploaded — re-indexing')
    } catch (error) {
      // The entry stays queued so "Keep both" is still reachable. Since F25 this
      // is no longer a permission failure for a colleague's document — the likely
      // causes are a concurrent replace (409) or a document mid-processing (400).
      setActionError(error instanceof Error ? error.message : 'Failed to upload new version')
    } finally {
      setIsWorking(false)
    }
  }

  const handleKeepBoth = async () => {
    setIsWorking(true)
    setActionError(null)
    try {
      await resolveDuplicateKeepBoth(pending.id)
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isWorking) dismissDuplicateUpload(pending.id)
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>This file already exists</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{existing.name}</span> is already in this
            folder. Keeping both leaves two live copies, and the assistant can cite either one — so
            a superseded document can end up in a draft.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex items-center gap-3 rounded-lg bg-muted/40 p-3">
            <FileTypeIcon mimeType={file.type} className="h-5 w-5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{existing.name}</p>
              <p className="text-meta">
                {existing.uploaderName ? `${existing.uploaderName} · ` : ''}
                {uploadedOn ? `${uploadedOn} · ` : ''}
                {formatFileSize(existing.size)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Badge variant="secondary" dot={false}>
                v{existing.version}
              </Badge>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              <Badge variant="outline" dot={false}>
                v{existing.version + 1}
              </Badge>
            </div>
          </div>

          {duplicateUploads.length > 1 && (
            <p className="text-meta">
              1 of {duplicateUploads.length} files need a decision.
            </p>
          )}

          {actionError && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-destructive">
                    Couldn't upload as a new version
                  </p>
                  <p className="mt-0.5 text-meta">{actionError}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => dismissDuplicateUpload(pending.id)}
            disabled={isWorking}
          >
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => void handleKeepBoth()} disabled={isWorking}>
            <Copy className="h-4 w-4 mr-2" />
            Keep both
          </Button>
          <Button onClick={() => void handleNewVersion()} disabled={isWorking}>
            {isWorking ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Upload as new version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
